import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import api from '../../services/api';
import CongratulationSendModal from '../Modals/CongratulationSendModal';
import { FiUsers, FiGitBranch, FiUserCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import ModalCloseButton from '../Common/ModalCloseButton';
import CompanyStructureModal from '../Modals/CompanyStructureModal';
import OnboardingModal from '../Modals/OnboardingModal';
import CompanyEmployeesListModal from '../Modals/CompanyEmployeesListModal';

// Styled компоненты в стиле модалки рейтинга
const ModalOverlay = styled.div.attrs({
  'data-overlay-id': 'congratulations-modal'
})`
  position: fixed !important;
  top: 0 !important;
  left: 320px !important;
  right: 0 !important;
  bottom: 0 !important;
  width: calc(100vw - 320px) !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  background: rgba(0,0,0,0.45) !important;
  display: flex !important;
  align-items: stretch !important;
  justify-content: flex-start !important;
  z-index: 100001 !important;
  transform: none !important;
  
  @media (max-width: 768px) {
    left: 0 !important;
    width: 100vw !important;
    padding: 12px;
    align-items: flex-start;
    justify-content: center;
  }
`;

const ModalContainer = styled.div`
  background: var(--modal-default-background, linear-gradient(135deg, #232931 0%, #181c22 100%));
  background-image: var(--modal-default-background-image, none);
  background-size: cover;
  background-position: center;
  border-radius: 0;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  min-width: 100% !important;
  min-height: 100% !important;
  box-shadow: none;
  color: var(--modal-default-text-color, #fff);
  font-size: var(--modal-default-text-size, 14px);
  font-family: var(--modal-default-font-family, inherit);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 1.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--modal-default-background, linear-gradient(135deg, #232931 0%, #181c22 100%));
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  border: none;
  border-radius: 8px;
  color: #232931;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 8px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(67, 233, 123, 0.3);
  }
`;

const tabBtnStyle = (active) => ({
  background: active ? 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)' : 'rgba(35,41,49,0.85)',
  color: active ? '#fff' : '#b2bec3',
  border: 'none',
  borderRadius: 12,
  fontWeight: 600,
  fontSize: '0.93em',
  padding: '6px 18px',
  cursor: 'pointer',
  marginRight: 8,
  boxShadow: active ? '0 2px 8px #43e97b33' : 'none',
  transition: 'background .2s,color .2s, box-shadow .2s',
  minWidth: '180px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/** Кнопки «Список сотрудников / Структура / Онбординг / Рейтинг» — как неактивные пункты главного меню (SidebarNav) */
const employeesMenuSidebarLikeStyle = {
  background: 'var(--sidebar-button-background, #232931)',
  color: 'var(--sidebar-text-color, #fff)',
  border: 'none',
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 'var(--sidebar-text-size, 1.08em)',
  fontFamily: 'var(--sidebar-font-family, inherit)',
  padding: '10px 18px',
  cursor: 'pointer',
  marginRight: 8,
  boxShadow: 'none',
  transition: 'all .18s',
  minWidth: '180px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

function getAge(birth_day, birth_month, birth_year) {
  if (!birth_day || !birth_month || !birth_year) return '-';
  const today = new Date();
  let age = today.getFullYear() - birth_year;
  const m = today.getMonth() + 1 - birth_month;
  if (m < 0 || (m === 0 && today.getDate() < birth_day)) {
    age--;
  }
  return age;
}

// Приводим URL аватарок к абсолютному, если приходит относительный путь от бэкенда
function normalizeAvatarUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return `${api.defaults.baseURL}${trimmed}`;
  return `${api.defaults.baseURL}/${trimmed}`;
}



function EmployeeList({ employees, loading, onEdit, onCongrats, setEmployees, usersByEmployeeId }) {
  const [search, setSearch] = useState('');

  // Проверка функций при рендере
  useEffect(() => {
    console.log('📋 EmployeeList rendered with:');
    console.log('  - onEdit:', typeof onEdit, onEdit);
    console.log('  - onCongrats:', typeof onCongrats, onCongrats);
    console.log('  - employees count:', employees?.length || 0);
  }, [onEdit, onCongrats, employees]);

  const safeEmployees = Array.isArray(employees) ? employees : [];
  const filteredEmployees = safeEmployees.filter((e) =>
    (e.first_name + ' ' + e.last_name).toLowerCase().includes(search.trim().toLowerCase())
  );

  // Функция для получения градиента по имени
  const getGradientByName = (name) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    ];
    const index = (name || '').length % gradients.length;
    return gradients[index];
  };

  return (
    <div style={{ width: '100%', marginLeft: 0, paddingLeft: 0, maxWidth: '100%' }}>
      <style>{`
        .styled-empl-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 1em;
          margin-left: 0;
          background: linear-gradient(135deg, rgba(44, 62, 80, 0.8) 0%, rgba(52, 73, 94, 0.8) 100%);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          border: 2px solid rgba(255, 255, 255, 0.1);
        }
        .styled-empl-table th, .styled-empl-table td {
          padding: 16px;
          text-align: left;
        }
        .styled-empl-table th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #ffffff;
          font-size: 1.1em;
          font-weight: 700;
          border-bottom: 2px solid rgba(255, 255, 255, 0.2);
        }
        .styled-empl-table tr {
          transition: all 0.3s ease;
        }
        .styled-empl-table tbody tr:nth-child(even) {
          background: rgba(255, 255, 255, 0.05);
        }
        .styled-empl-table tbody tr:hover {
          background: rgba(67, 233, 123, 0.1);
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }
        .styled-empl-table td {
          color: #ffffff;
          font-size: 1em;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        .styled-empl-table td:last-child {
          white-space: nowrap;
        }
      `}</style>
      <input
        type="text"
        placeholder="Поиск сотрудников..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: '12px 16px',
          marginBottom: '20px',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          fontSize: '1em',
          marginLeft: 0,
          background: 'rgba(255, 255, 255, 0.05)',
          color: '#ffffff',
          outline: 'none',
        }}
      />
      <table className="styled-empl-table">
        <thead>
          <tr>
            <th>Фото</th>
            <th>Имя</th>
            <th>Должность</th>
            <th>Дата рождения</th>
            <th>Возраст</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '1.1em' }}>
                Загрузка...
              </td>
            </tr>
          ) : filteredEmployees.length === 0 ? (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '1.1em' }}>
                Сотрудники не найдены
              </td>
            </tr>
          ) : (
            filteredEmployees.map((emp) => {
              const user = usersByEmployeeId[String(emp.id)] || {};
              const avatarUrl = normalizeAvatarUrl(user.avatar);
              const initials = `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`;
              
              // Логирование при рендере каждой строки
              console.log(`📝 Rendering row for employee:`, emp.id, emp.first_name, emp.last_name);
              console.log(`📝 onEdit:`, typeof onEdit, onEdit);
              console.log(`📝 onCongrats:`, typeof onCongrats, onCongrats);

              return (
                <tr 
                  key={emp.id}
                  onClick={(e) => {
                    // Логируем клик по строке для отладки
                    console.log('📋 Row clicked for employee:', emp.id);
                    // НЕ останавливаем всплытие здесь, чтобы кнопки могли получить событие
                  }}
                  style={{ position: 'relative' }}
                >
                  <td>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: avatarUrl
                          ? `url(${avatarUrl}) center/cover`
                          : getGradientByName(`${emp.first_name} ${emp.last_name}`),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.9em',
                      }}
                    >
                      {!avatarUrl && initials}
                    </div>
                  </td>
                  <td>{emp.first_name} {emp.last_name}</td>
                  <td>{emp.position}</td>
                  <td>
                    {emp.birth_day && emp.birth_month
                      ? `${emp.birth_day}.${emp.birth_month}${emp.birth_year ? `.${emp.birth_year}` : ''}`
                      : '-'}
                  </td>
                  <td>{getAge(emp.birth_day, emp.birth_month, emp.birth_year)}</td>
                  <td 
                    style={{ 
                      position: 'relative', 
                      zIndex: 100,
                      pointerEvents: 'auto'
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('✏️ Редактировать clicked for employee:', emp);
                        console.log('✏️ onEdit function:', typeof onEdit, onEdit);
                        if (emp && emp.id) {
                          try {
                            onEdit(emp);
                            console.log('✅ onEdit called successfully');
                          } catch (err) {
                            console.error('❌ Error calling onEdit:', err);
                            alert('Ошибка при открытии модалки редактирования: ' + err.message);
                          }
                        } else {
                          console.error('❌ Invalid employee object for edit:', emp);
                          alert('Ошибка: данные сотрудника некорректны');
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('✏️ Редактировать mouseDown');
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        console.log('✏️ Редактировать touchStart');
                      }}
                      style={{ 
                        marginRight: '8px',
                        position: 'relative',
                        zIndex: 1000,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#232931',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        padding: '4px 8px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Редактировать
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🎉 Поздравить clicked for employee:', emp);
                        console.log('🎉 onCongrats function:', typeof onCongrats, onCongrats);
                        if (emp && emp.id) {
                          try {
                            onCongrats(emp);
                            console.log('✅ onCongrats called successfully');
                          } catch (err) {
                            console.error('❌ Error calling onCongrats:', err);
                            alert('Ошибка при открытии модалки поздравления: ' + err.message);
                          }
                        } else {
                          console.error('❌ Invalid employee object:', emp);
                          alert('Ошибка: данные сотрудника некорректны');
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🎉 Поздравить mouseDown');
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        console.log('🎉 Поздравить touchStart');
                      }}
                      style={{ 
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        marginRight: '8px',
                        position: 'relative',
                        zIndex: 1000,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#232931',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        padding: '4px 8px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Поздравить
                    </button>
                    <ActionButton
                      style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' }}
                      onClick={async () => {
                        if (window.confirm('Удалить сотрудника и связанного пользователя?')) {
                          try {
                            console.log('🗑️ Deleting employee with id:', emp.id);
                            const response = await api.delete(`/api/employees/${emp.id}`);
                            console.log('✅ Delete response:', response);
                            
                            // Проверяем, что ответ успешный
                            if (response && response.status >= 200 && response.status < 300) {
                              console.log('✅ Employee deleted successfully');
                              
                              // Обновляем список сотрудников
                              setEmployees((prev) => prev.filter(e => e.id !== emp.id));
                              
                              // Обновляем карту пользователей
                              const usersMap = usersByEmployeeId || {};
                              const user = usersMap[String(emp.id)];
                              if (user && user.id) {
                                try { 
                                  console.log('🗑️ Deleting linked user with id:', user.id);
                                  await api.delete(`/api/users/${user.id}`);
                                  console.log('✅ Linked user deleted');
                                } catch(e) {
                                  console.error('⚠️ Error deleting linked user:', e);
                                  // Не показываем ошибку пользователю, т.к. сотрудник уже удален
                                }
                              }
                              
                              // Перезагружаем список сотрудников для синхронизации
                              setTimeout(async () => {
                                try {
                                  const empRes = await api.get('/api/employees');
                                  const empArr = Array.isArray(empRes.data) ? empRes.data : [];
                                  setEmployees(empArr);
                                } catch (e) {
                                  console.error('⚠️ Error reloading employees:', e);
                                }
                              }, 100);
                            } else {
                              throw new Error('Неожиданный ответ сервера');
                            }
                          } catch (err) {
                            console.error('❌ Error deleting employee:', err);
                            console.error('❌ Error details:', {
                              message: err.message,
                              response: err.response,
                              status: err.response?.status,
                              data: err.response?.data,
                              config: err.config
                            });
                            
                            let errorMessage = 'Неизвестная ошибка';
                            if (err.response) {
                              // Сервер вернул ответ с ошибкой
                              errorMessage = err.response.data?.error || err.response.data?.message || `Ошибка ${err.response.status}`;
                            } else if (err.request) {
                              // Запрос был отправлен, но ответа не получено
                              errorMessage = 'Не удалось получить ответ от сервера';
                            } else {
                              // Ошибка при настройке запроса
                              errorMessage = err.message || 'Ошибка при отправке запроса';
                            }
                            
                            alert(`Ошибка при удалении сотрудника: ${errorMessage}`);
                          }
                        }
                      }}
                    >
                      Удалить
                    </ActionButton>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [birthDate, setBirthDate] = useState(
    user.birth_year && user.birth_month && user.birth_day
      ? `${user.birth_year}-${String(user.birth_month).padStart(2, '0')}-${String(user.birth_day).padStart(2, '0')}`
      : ''
  );
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData();
    formData.append('id', user.id);
    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    if (birthDate) {
      const [year, month, day] = birthDate.split('-');
      formData.append('birth_year', year);
      formData.append('birth_month', month);
      formData.append('birth_day', day);
    }
    if (avatar) {
      formData.append('avatar', avatar);
    }
    try {
      const response = await api.post('/api/congratulations/edit-employee', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('✅ Employee updated:', response);
      
      // Сохраняем изменения - onSaved вызовет перезагрузку списка
      onSaved();
    } catch (err) {
      console.error('❌ Error updating employee:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Неизвестная ошибка';
      alert(`Ошибка при сохранении: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        zIndex: 100002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
          color: '#fff',
          borderRadius: 20,
          minWidth: 500,
          maxWidth: 600,
          width: '90%',
          padding: 0,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          position: 'relative',
          fontSize: '1em',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Заголовок в стиле модалки рейтинга */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 32px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}>
          <h2 style={{
            margin: 0,
            color: '#ffffff',
            fontSize: '1.5rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            👤 Редактировать сотрудника
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '20px',
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '32px' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ 
                  fontWeight: 600, 
                  color: '#ffffff', 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '1rem'
                }}>Имя:</label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    borderRadius: 12, 
                    border: '2px solid rgba(255, 255, 255, 0.1)', 
                    padding: '12px 16px', 
                    fontWeight: 500, 
                    fontSize: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  fontWeight: 600, 
                  color: '#ffffff', 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '1rem'
                }}>Фамилия:</label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    borderRadius: 12, 
                    border: '2px solid rgba(255, 255, 255, 0.1)', 
                    padding: '12px 16px', 
                    fontWeight: 500, 
                    fontSize: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  fontWeight: 600, 
                  color: '#ffffff', 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '1rem'
                }}>Дата рождения:</label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    borderRadius: 12, 
                    border: '2px solid rgba(255, 255, 255, 0.1)', 
                    padding: '12px 16px', 
                    fontWeight: 500, 
                    fontSize: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  fontWeight: 600, 
                  color: '#ffffff', 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '1rem'
                }}>Фото сотрудника:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAvatar(e.target.files[0])}
                  style={{ 
                    width: '100%',
                    borderRadius: 12, 
                    border: '2px solid rgba(255, 255, 255, 0.1)', 
                    padding: '12px 16px', 
                    fontWeight: 500, 
                    fontSize: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                />
              </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
            <ActionButton
              type="button"
              onClick={onClose}
              style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' }}
            >
              Отмена
            </ActionButton>
            <ActionButton
              type="submit"
              disabled={saving}
              style={{ background: 'linear-gradient(135deg, #43e97b 0%, #2193b0 100%)' }}
            >
              {saving ? 'Сохраняю...' : 'Применить'}
            </ActionButton>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ onClose, onAdded }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // только success

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
      // Ошибки не отображаем
    const formData = new FormData();
    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    if (birthDate) {
      const [year, month, day] = birthDate.split('-');
      formData.append('birth_year', year);
      formData.append('birth_month', month);
      formData.append('birth_day', day);
    }
    if (avatar) {
      formData.append('avatar', avatar);
    }
    try {
      const res = await api.post('/api/congratulations/add-employee', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.success) {
        setMessage('Сотрудник и аватарка успешно добавлены!');
        setMessageType('success');
        setTimeout(() => {
          onAdded();
        }, 1200);
      } else {
        setMessage('Сотрудник добавлен, но аватарка не загружена.');
          // Ошибки не отображаем
      }
    } catch (err) {
      setMessage('Ошибка при добавлении пользователя или аватарки');
        // Ошибки не отображаем
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(34,40,49,0.82)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#232526',
          color: '#fff',
          borderRadius: 24,
          minWidth: 420,
          maxWidth: 520,
          width: '100%',
          padding: '38px 38px 28px 38px',
          boxShadow: '0 8px 40px #2193b044, 0 0 16px #43e97b55',
          position: 'relative',
          fontSize: '0.93em',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 18,
            right: 24,
            fontSize: 28,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#2193b0',
          }}
        >
          ×
        </button>
        <h3 style={{ marginBottom: 18, color: '#43e97b', fontWeight: 800, fontSize: '1.22em' }}>
          Добавить пользователя
        </h3>
        <form onSubmit={handleSubmit}>
          <input
            placeholder="Имя"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            style={{
              width: '100%',
              marginBottom: 12,
              padding: 10,
              borderRadius: 8,
              border: '2px solid #43e97b',
              fontWeight: 600,
              fontSize: '0.93em',
            }}
          />
          <input
            placeholder="Фамилия"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            style={{
              width: '100%',
              marginBottom: 12,
              padding: 10,
              borderRadius: 8,
              border: '2px solid #43e97b',
              fontWeight: 600,
              fontSize: '0.93em',
            }}
          />
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            required
            style={{
              width: '100%',
              marginBottom: 12,
              padding: 10,
              borderRadius: 8,
              border: '2px solid #2193b0',
              fontWeight: 600,
              fontSize: '0.93em',
            }}
          />
          <label style={{ fontWeight: 600, color: '#43e97b', marginBottom: 6 }}>Аватар:</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setAvatar(e.target.files[0])}
            style={{ borderRadius: 8, border: '2px solid #43e97b', padding: 8, background: '#fff', color: '#2193b0' }}
          />
          {message && (
            <div
              style={{
                marginTop: 12,
                color: messageType === 'success' ? '#43e97b' : '#e74c3c',
                fontWeight: 600,
              }}
            >
              {message}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
            <button
              type="button"
              onClick={onClose}
              style={{ ...tabBtnStyle(false), background: '#e74c3c', color: '#fff' }}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                ...tabBtnStyle(false),
                background: 'linear-gradient(135deg, #43e97b 0%, #2193b0 100%)',
                color: '#fff',
              }}
            >
              {saving ? 'Сохраняю...' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CongratulationsModal({ onClose }) {
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersByEmployeeId, setUsersByEmployeeId] = useState({});
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [congratsUser, setCongratsUser] = useState(null);
  const [showCompanyStructure, setShowCompanyStructure] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCompanyEmployeesList, setShowCompanyEmployeesList] = useState(false);
  const [actionsBarExpanded, setActionsBarExpanded] = useState(true);
  const [viewportNarrow, setViewportNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const onResize = () => setViewportNarrow(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Применяем стили для полного экрана через DOM manipulation
  useEffect(() => {
    const applyStyles = () => {
      // Ищем элемент по ID
      const overlay = document.getElementById('congratulations-modal-overlay') || overlayRef.current;
      if (overlay) {
        overlay.style.setProperty('left', '320px', 'important');
        overlay.style.setProperty('width', 'calc(100vw - 320px)', 'important');
        overlay.style.setProperty('right', '0', 'important');
        overlay.style.setProperty('top', '0', 'important');
        overlay.style.setProperty('bottom', '0', 'important');
        overlay.style.setProperty('height', '100vh', 'important');
        overlay.style.setProperty('margin', '0', 'important');
        overlay.style.setProperty('padding', '0', 'important');
        overlay.style.setProperty('z-index', '100001', 'important');
        overlay.style.setProperty('position', 'fixed', 'important');
        overlay.style.setProperty('display', 'flex', 'important');
        overlay.style.setProperty('align-items', 'stretch', 'important');
        overlay.style.setProperty('justify-content', 'flex-start', 'important');
        overlay.style.setProperty('background', 'rgba(0,0,0,0.45)', 'important');
      }
      
      const container = containerRef.current || (overlay?.firstElementChild);
      if (container) {
        container.style.setProperty('border-radius', '0', 'important');
        container.style.setProperty('box-shadow', 'none', 'important');
        container.style.setProperty('margin', '0', 'important');
        container.style.setProperty('width', '100%', 'important');
        container.style.setProperty('height', '100%', 'important');
        container.style.setProperty('max-width', '100%', 'important');
        container.style.setProperty('max-height', '100%', 'important');
        container.style.setProperty('min-width', '100%', 'important');
        container.style.setProperty('min-height', '100%', 'important');
      }
    };
    
    // Применяем стили сразу и с задержками
    applyStyles();
    const timeoutId1 = setTimeout(applyStyles, 10);
    const timeoutId2 = setTimeout(applyStyles, 50);
    const timeoutId3 = setTimeout(applyStyles, 100);
    const timeoutId4 = setTimeout(applyStyles, 200);
    const timeoutId5 = setTimeout(applyStyles, 500);
    const timeoutId6 = setTimeout(applyStyles, 1000);
    
    // MutationObserver для отслеживания изменений в DOM
    const observer = new MutationObserver(() => {
      applyStyles();
    });
    
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    
    // Добавляем глобальные стили в начало head для максимального приоритета
    const styleId = 'congratulations-modal-overlay-styles';
    let existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      #congratulations-modal-overlay,
      div[data-overlay-id="congratulations-modal"],
      div[class*="ModalOverlay"] {
        left: 320px !important;
        width: calc(100vw - 320px) !important;
        right: 0 !important;
        background: rgba(0,0,0,0.45) !important;
        z-index: 100001 !important;
        margin: 0 !important;
        padding: 0 !important;
        top: 0 !important;
        bottom: 0 !important;
        height: 100vh !important;
        position: fixed !important;
        display: flex !important;
        align-items: stretch !important;
        justify-content: flex-start !important;
        transform: none !important;
      }
      #congratulations-modal-overlay > div,
      div[data-overlay-id="congratulations-modal"] > div,
      div[class*="ModalOverlay"] > div[class*="ModalContainer"] {
        border-radius: 0 !important;
        box-shadow: none !important;
        margin: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        min-width: 100% !important;
        min-height: 100% !important;
        pointer-events: auto !important;
      }
      #congratulations-modal-overlay *,
      div[data-overlay-id="congratulations-modal"] * {
        pointer-events: auto !important;
      }
    `;
    // Вставляем в начало head для максимального приоритета
    if (document.head.firstChild) {
      document.head.insertBefore(style, document.head.firstChild);
    } else {
      document.head.appendChild(style);
    }
    
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      clearTimeout(timeoutId5);
      clearTimeout(timeoutId6);
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  // Логирование изменений congratsUser для отладки
  useEffect(() => {
    if (congratsUser) {
      console.log('🎉 congratsUser set to:', congratsUser);
    } else {
      console.log('🔒 congratsUser cleared');
    }
  }, [congratsUser]);

  // Защита: employees всегда массив
  const safeEmployees = Array.isArray(employees) ? employees : [];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/employees'),
      api.get('/api/users').catch(() => ({ data: [] }))
    ])
      .then(([empRes, usersRes]) => {
        const empArr = Array.isArray(empRes.data) ? empRes.data : [];
        const usersArr = Array.isArray(usersRes.data) ? usersRes.data : [];
        const map = {};
        usersArr.forEach(u => {
          const key = String(u.employee_id || u.employeeId || '');
          if (key) map[key] = u;
        });
        setUsersByEmployeeId(map);
        setEmployees(empArr);
      })
      .catch(() => {
        setUsersByEmployeeId({});
        setEmployees([]);
      })
      .finally(() => setLoading(false));
  }, [showAddModal, editUser]);

  const filteredEmployees = safeEmployees.filter((e) =>
    (e.first_name + ' ' + e.last_name).toLowerCase().includes(search.trim().toLowerCase())
  );
  console.log('employees:', employees);
  console.log('safeEmployees:', safeEmployees);
  console.log('filteredEmployees:', filteredEmployees);

  return (
    <ModalOverlay ref={overlayRef} id="congratulations-modal-overlay" data-overlay-id="congratulations-modal">
      <ModalContainer ref={containerRef}>
        <ModalHeader style={{ padding: '8px 24px' }}>
          <ModalTitle>
            <FiUsers />
            Сотрудники
          </ModalTitle>
          <div style={{ position: 'relative', width: 52, flexShrink: 0 }}>
            <ModalCloseButton onClick={onClose} aria-label="Закрыть" />
          </div>
        </ModalHeader>
        <ModalBody style={{ paddingTop: 0 }}>
      <div style={{
        margin: '-14px 0 16px 0',
        width: '100%',
        maxWidth: '100%',
        padding: '0 16px',
        boxSizing: 'border-box',
        marginLeft: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: actionsBarExpanded ? 12 : 0,
        }}>
          <button
            type="button"
            aria-expanded={actionsBarExpanded}
            aria-label={actionsBarExpanded ? 'Свернуть панель действий' : 'Показать панель действий'}
            onClick={() => setActionsBarExpanded((v) => !v)}
            style={{
              ...employeesMenuSidebarLikeStyle,
              minWidth: 'auto',
              marginRight: 0,
              padding: '10px 14px',
              gap: 6,
            }}
          >
            {actionsBarExpanded ? <FiChevronUp size={18} aria-hidden /> : <FiChevronDown size={18} aria-hidden />}
            {actionsBarExpanded ? 'Свернуть' : 'Действия'}
          </button>
          <input
            type="text"
            placeholder="Быстрый поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: '1 1 200px',
              minWidth: 160,
              padding: '7px 16px',
              borderRadius: 10,
              border: '2px solid #43e97b',
              fontSize: '0.97em',
              fontWeight: 600,
              outline: 'none',
            }}
          />
        </div>
        {actionsBarExpanded && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}>
            <button
              style={employeesMenuSidebarLikeStyle}
              onClick={() => setShowCompanyEmployeesList(true)}
            >
              Список сотрудников
            </button>
            <button
              style={employeesMenuSidebarLikeStyle}
              onClick={() => window.dispatchEvent(new Event('show-employee-rating'))}
            >
              Рейтинг успеваемости
            </button>
            <button
              style={employeesMenuSidebarLikeStyle}
              onClick={() => {
                console.log('🔍 Кнопка "Структура компании" нажата в CongratulationsModal');
                setShowCompanyStructure(true);
              }}
            >
              <FiGitBranch size={18} />
              🌳 Структура компании
            </button>
            <button
              style={employeesMenuSidebarLikeStyle}
              onClick={() => {
                console.log('🎯 Кнопка "Онбординг" нажата');
                setShowOnboarding(true);
              }}
            >
              <FiUserCheck size={18} />
              📋 Онбординг
            </button>
          </div>
        )}
      </div>
        <div style={{ width: '100%', maxWidth: '100%', margin: 0, padding: '0 16px', boxSizing: 'border-box' }}>
          <div style={{ marginLeft: 0 }}>
            {(() => {
              // Проверка функций перед рендерингом
              console.log('🔍 Before rendering EmployeeList:');
              console.log('  - setEditUser:', typeof setEditUser, setEditUser);
              console.log('  - setCongratsUser:', typeof setCongratsUser, setCongratsUser);
              console.log('  - filteredEmployees:', filteredEmployees?.length || 0);
              console.log('  - loading:', loading);
              
              if (typeof setEditUser !== 'function') {
                console.error('❌ setEditUser is not a function!', setEditUser);
              }
              if (typeof setCongratsUser !== 'function') {
                console.error('❌ setCongratsUser is not a function!', setCongratsUser);
              }
              
              return (
                <EmployeeList
                  employees={filteredEmployees}
                  loading={loading}
                  onEdit={(emp) => {
                    console.log('🔧 onEdit wrapper called with:', emp);
                    if (typeof setEditUser === 'function') {
                      console.log('✅ Calling setEditUser');
                      setEditUser(emp);
                    } else {
                      console.error('❌ setEditUser is not a function!', setEditUser);
                    }
                  }}
                  onCongrats={(emp) => {
                    console.log('🎉 onCongrats wrapper called with:', emp);
                    if (typeof setCongratsUser === 'function') {
                      console.log('✅ Calling setCongratsUser');
                      setCongratsUser(emp);
                    } else {
                      console.error('❌ setCongratsUser is not a function!', setCongratsUser);
                    }
                  }}
                  setEmployees={setEmployees}
                  usersByEmployeeId={usersByEmployeeId}
                />
              );
            })()}
          </div>
        </div>
{showAddModal && <AddUserModal onClose={() => setShowAddModal(false)} onAdded={() => setShowAddModal(false)} />}
{editUser && (
  <EditUserModal 
    user={editUser} 
    onClose={() => {
      console.log('🔒 Closing EditUserModal, editUser:', editUser);
      setEditUser(null);
    }} 
    onSaved={() => {
      console.log('✅ User saved, reloading...');
      setEditUser(null);
      // Перезагружаем список сотрудников
      setLoading(true);
      Promise.all([
        api.get('/api/employees'),
        api.get('/api/users').catch(() => ({ data: [] }))
      ])
        .then(([empRes, usersRes]) => {
          const empArr = Array.isArray(empRes.data) ? empRes.data : [];
          const usersArr = Array.isArray(usersRes.data) ? usersRes.data : [];
          const map = {};
          usersArr.forEach(u => {
            const key = String(u.employee_id || u.employeeId || '');
            if (key) map[key] = u;
          });
          setUsersByEmployeeId(map);
          setEmployees(empArr);
        })
        .catch(() => {
          setUsersByEmployeeId({});
          setEmployees([]);
        })
        .finally(() => setLoading(false));
    }} 
  />
)}
{congratsUser && (
  <CongratulationSendModal
    user={congratsUser}
    open={!!congratsUser}
    onClose={() => {
      console.log('🔒 Closing CongratulationSendModal, congratsUser:', congratsUser);
      setCongratsUser(null);
    }}
    onSent={() => {
      console.log('✅ Congratulation sent');
      setCongratsUser(null);
      window.dispatchEvent(new CustomEvent('news-published'));
    }}
  />
)}
{showCompanyStructure && (
  <CompanyStructureModal
    open={showCompanyStructure}
    onClose={() => setShowCompanyStructure(false)}
  />
)}
{showOnboarding && (
  <OnboardingModal
    open={showOnboarding}
    onClose={() => setShowOnboarding(false)}
    isMobile={viewportNarrow}
  />
)}
{showCompanyEmployeesList && (
  <CompanyEmployeesListModal
    open={showCompanyEmployeesList}
    onClose={() => setShowCompanyEmployeesList(false)}
  />
)}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
}